<template>
  <div class="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
    <span class="tw-text-sm tw-font-medium tw-text-sf-neutral-darker-1">
      {{ field.label ?? field.key }}
    </span>
    <SfSelect
      :options="options"
      :model-value="value"
      :disabled="locked"
      :aria-label="accessibleName"
      :data-testid="`column-field-${field.key}`"
      @update:model-value="(newValue) => emit('update', newValue)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import SfSelect from '@app/shared/vue/components/sf-select/sf-select.vue'

import type { FieldSchema, SelectOption } from '../types'
import { useColumnEditorI18n } from '../use-column-editor-i18n'

const props =
  defineProps<{
    field: FieldSchema
    value: string
    // The values this field may take given the rest of the column (see
    // column-constraints.ts). Undefined when the column's other values narrow
    // nothing: every option stays open.
    enabledValues?: string[]
  }>()

const emit =
  defineEmits<{
    update: [value: string]
  }>()

const t = useColumnEditorI18n()

// A shipped option plus whether the rest of the column leaves it open.
interface FieldOption extends SelectOption {
  disabled: boolean
}

// A stored value can sit outside its field's option list — a column whose
// `show` is still a budget set that has since been deleted, say. Left alone the
// control would show no label at all, so the drawer would say nothing about a
// value it still holds and would hand it to a later Apply. Carry the stored
// value as an option of its own, named for what it is. Picking any real option
// drops it.
const options = computed<FieldOption[]>(() => {
  const shipped = props.field.options.some(
    (option) => option.value === props.value
  )
    ? props.field.options
    : [
        { label: t('unavailable_option'), value: props.value },
        ...props.field.options,
      ]

  return shipped.map((option) => ({ ...option, disabled: !isEnabled(option) }))
})

// Ruled-out values stay in the list, greyed out, rather than disappearing: the
// point is for the user to see that the value exists and that this column can't
// have it. That can include the value the column currently holds: the rules are
// the form's, so a column saved elsewhere can arrive already contradicting them
// and opens showing its own value greyed out until the user picks another.
const isEnabled = (option: SelectOption): boolean =>
  !props.enabledValues || props.enabledValues.includes(option.value)

// Nothing left to choose, so there is nothing to open. A field the rules say
// nothing about is never locked, even if its schema only offers one option.
//
// A column that arrived already contradicting the rules keeps its field open
// even so: the one value left is not the one it holds, so there is a change to
// make and locking the control would be trapping it on the value the rules
// reject — with no other field to edit its way out of.
const locked = computed(
  () =>
    !!props.enabledValues &&
    props.enabledValues.length <= 1 &&
    props.enabledValues[0] === props.value
)

// Both operands of a difference column show the same visible labels, so the
// label alone would name two controls. The suffix keeps the spoken name unique
// while still starting with the text on screen.
const operandSuffix = computed(() => {
  if (props.field.group === 'a') return t('difference.value_a')
  if (props.field.group === 'b') return t('difference.value_b')
  return null
})

const accessibleName = computed(() => {
  const label = props.field.label ?? props.field.key
  return operandSuffix.value ? `${label} (${operandSuffix.value})` : label
})
</script>
